<template>
  <div :class="rootClass" ref="rootEl" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <video
      ref="videoRef"
      :src="resolvedSrc"
      :poster="poster"
      :preload="preload ?? 'metadata'"
      :autoplay="autoplay ?? true"
      :muted="muted"
      :loop="loop"
      playsinline
      webkit-playsinline="true"
      x-webkit-airplay="allow"
      x5-video-player-type="h5"
      x5-video-player-fullscreen="true"
      @loadedmetadata="onVideoMeta"
      @play="onVideoPlay"
      @pause="onVideoPause"
      @playing="onVideoPlaying"
      @timeupdate="onVideoTimeUpdate"
      @waiting="onVideoWaiting"
      @canplay="onVideoCanPlay"
      @ended="onVideoEnded"
    />

    <!-- 中央播放/暂停按钮 -->
    <div
      v-if="showBigButton && (isMobileOrTablet || hovered)"
      class="e-video-player__center-buttons"
    >
      <SiteSpinner v-if="loading && playing" />
      <button
        v-else-if="!loading && !playing"
        class="e-video-player__center-button"
        @click="onPlayClick"
      >
        <Icon name="play" size="xs" />
      </button>
      <button
        v-else-if="!loading && playing && !isMobileOrTablet"
        class="e-video-player__center-button"
        @click="onPauseClick"
      >
        <Icon name="pause" size="xs" />
      </button>
    </div>

    <!-- 底部控制栏 -->
    <Transition name="fade">
      <div v-if="controls" v-show="isMobileOrTablet || hovered" class="e-video-player__controls">
        <Icon v-if="ended" class="hand" name="replay" @click="onReplay" />
        <Icon v-else-if="!playing" class="hand" name="play" @click="onPlayClick" />
        <Icon v-else class="hand" name="pause" @click="onPauseClick" />

        <div class="e-video-player__time">{{ formatDuration(currentTime) }}</div>

        <div
          class="e-video-player__control-bar"
          @click="onControlBarClick"
          @mouseenter="onBarMouseEnter"
          @mouseleave="onBarMouseLeave"
          @mousemove="onBarMouseMove"
        >
          <div class="e-video-player__control-track" />
          <div class="e-video-player__control-progress" :style="progressStyle" />
          <div class="e-video-player__control-thumb" :style="controlThumbStyle" />
          <div v-if="barHovered" class="e-video-player__control-hover-pop" :style="hoverPopStyle">
            {{ formatDuration(duration * (hoverProgress / 100)) }}
          </div>
        </div>

        <div class="e-video-player__time">{{ formatDuration(duration) }}</div>

        <Icon class="hand" :name="muted ? 'mute' : 'un-muted'" @click="toggleMuted" />
        <Icon
          class="hand"
          :name="isFullScreen ? 'exit-full-screen' : 'full-screen'"
          @click="toggleFullScreen"
        />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import Icon from '@/elements/Icon.vue';
import utils from '@/utils';
import useDevice from '@/hooks/useDevice';
import { VideoPlayerProps } from '@/types/elements/video-player';

const props = withDefaults(defineProps<VideoPlayerProps>(), {
  loop: true,
  controls: false,
  autoplay: true,
});

// ---------- Emits ----------
const emit = defineEmits(['play', 'pause', 'ended']);

// ---------- Expose（对应 forwardRef / useImperativeHandle）----------
defineExpose({ play, pause, updateHovered });

// ---------- Device ----------
const { deviceState, deviceComputes } = useDevice();
const { isMobileOrTablet } = deviceComputes;
const { browser } = deviceState;
// ---------- Refs ----------
const rootEl = ref<HTMLDivElement | null>(null);
const videoRef = ref<HTMLVideoElement | null>(null);

// ---------- State ----------
const hovered = ref(false);
const playing = ref(false);
const loading = ref(true);
const ended = ref(false);
// autoplay 场景初始必须静音，浏览器才放行自动播放；首次用户交互后解除静音（见 onUserInteraction）。
// 注意：不能在模板写死 muted="false"——muted 是布尔属性，属性存在即静音，与取值无关。
const muted = ref(!!props.autoplay);
const duration = ref(0);
const currentTime = ref(0);
const progress = ref(0);
const isFullScreen = ref(false);
const barHovered = ref(false);
const hoverProgress = ref(0);

// ---------- Computed ----------
const resolvedSrc = computed(() => {
  return props.src;
});

const rootClass = computed(() => ['e-video-player', props.className, { 'as-bg': props.asBg }]);

const progressStyle = computed(() => ({ width: `${progress.value}%` }));
const controlThumbStyle = computed(() => ({ left: `${progress.value}%` }));
const hoverPopStyle = computed(() => ({ left: `${hoverProgress.value}%` }));

// ---------- 工具函数 ----------
function formatDuration(seconds: number) {
  return utils.math.formatDuration(seconds);
}

// ---------- 播放控制 ----------
function onPlayClick() {
  videoRef.value?.play();
}

function onPauseClick() {
  videoRef.value?.pause();
}

function onReplay() {
  if (!videoRef.value) return;
  ended.value = false;
  playing.value = true;
  videoRef.value.currentTime = 0;
  videoRef.value.play().catch(() => {});
}

// ---------- 视频事件 ----------
function onVideoMeta(e: Event) {
  duration.value = (e.target as HTMLVideoElement).duration;
}

function onVideoPlay() {
  playing.value = true;
  loading.value = false;
  emit('play');
}

function onVideoPause() {
  playing.value = false;
  emit('pause');
}

function onVideoPlaying() {
  loading.value = false;
}

function onVideoWaiting(e: Event) {
  const el = e.target as HTMLVideoElement;
  if (el.currentTime < el.duration) {
    loading.value = true;
  }
}

function onVideoCanPlay() {
  loading.value = false;
}

function onVideoEnded() {
  playing.value = false;
  ended.value = true;
  loading.value = false;
  progress.value = 100;
  emit('ended');
}

function onVideoTimeUpdate(e: Event) {
  const el = e.target as HTMLVideoElement;
  const p =
    el.currentTime === el.duration
      ? 100
      : Number(((el.currentTime * 100) / el.duration).toFixed(2));
  currentTime.value = el.currentTime;
  ended.value = el.currentTime === el.duration;
  progress.value = p;
}

// ---------- 进度条交互 ----------
function onControlBarClick(e: MouseEvent) {
  if (!videoRef.value || videoRef.value.duration <= 0) return;
  const $bar = e.currentTarget as HTMLDivElement;
  const barRect = $bar.getBoundingClientRect();
  const progressNum = (e.clientX - barRect.left) / $bar.scrollWidth;
  const time = videoRef.value.duration * progressNum;
  if (isNaN(time)) return;
  videoRef.value.currentTime = time;
  currentTime.value = time;
  progress.value = Number((progressNum * 100).toFixed(2));
}

function setBarHoveredProgress(e: MouseEvent) {
  if (!videoRef.value) return;
  const $bar = e.currentTarget as HTMLDivElement;
  const progressNum = (e.clientX - $bar.offsetLeft) / $bar.scrollWidth;
  hoverProgress.value = Number((progressNum * 100).toFixed(2));
}

function onBarMouseEnter(e: MouseEvent) {
  barHovered.value = true;
  setBarHoveredProgress(e);
}

function onBarMouseLeave() {
  barHovered.value = false;
}

function onBarMouseMove(e: MouseEvent) {
  setBarHoveredProgress(e);
}

// ---------- 鼠标悬停 ----------
function onMouseEnter() {
  hovered.value = true;
}

function onMouseLeave() {
  hovered.value = false;
}

// ---------- 静音 ----------
function toggleMuted() {
  if (!videoRef.value) return;
  muted.value = !muted.value;
  videoRef.value.muted = muted.value;
}

// ---------- 全屏 ----------
function toggleFullScreen() {
  if (!rootEl.value) return;
  const triggerEl = isMobileOrTablet || browser === 'safari' ? videoRef.value : rootEl.value;

  const _isFullScreen = !isFullScreen.value;
  if (triggerEl !== videoRef.value) {
    isFullScreen.value = _isFullScreen;
  }
  if (_isFullScreen) {
    utils.dom.goFullScreen(triggerEl!);
  } else {
    utils.dom.exitFullScreen();
  }
}

// ---------- Expose 实现 ----------
function play(seekTo?: number) {
  if (!videoRef.value) return;
  if (seekTo !== undefined && seekTo >= 0) {
    videoRef.value.currentTime = seekTo;
    currentTime.value = seekTo;
  }
  videoRef.value.play();
}

function pause() {
  playing.value = false;
  videoRef.value?.pause();
}

function updateHovered(value: boolean) {
  hovered.value = value;
}

// ---------- 全屏变化监听 ----------
function onFullScreenChange() {
  isFullScreen.value = !!document.fullscreenElement;
}

// ---------- 用户交互后解除静音并有声播放 ----------
// 浏览器 autoplay 策略：unmuted play 必须由用户交互触发。
// 监听首次用户交互，交互后解除静音并 play()，此时浏览器会放行走声音；
// 若有声播放仍被拦截（如 scroll 不构成用户激活），回退为静音播放保证画面不中断。
const hasUserInteracted = ref(false);
const userInteractionEvents = ['click', 'scroll', 'keydown', 'touchstart', 'pointerdown'];
function onUserInteraction() {
  if (hasUserInteracted.value) return;
  hasUserInteracted.value = true;
  const video = videoRef.value;
  if (video && props.autoplay) {
    video.muted = false;
    muted.value = false;
    video.play().catch(() => {
      video.muted = true;
      muted.value = true;
      return video.play().catch(() => {});
    });
  }
  userInteractionEvents.forEach((evt) => {
    document.removeEventListener(evt, onUserInteraction, { passive: true } as EventListenerOptions);
  });
}

onMounted(() => {
  const el = rootEl.value!;
  const videoEl = videoRef.value as any;

  el.addEventListener('fullscreenchange', onFullScreenChange, { passive: true });
  el.addEventListener('fullscreenerror', onFullScreenChange, { passive: true });
  videoEl?.addEventListener('fullscreenchange', onFullScreenChange, { passive: true });
  videoEl?.addEventListener('fullscreenerror', onFullScreenChange, { passive: true });

  // 注册全局用户交互监听，首次交互时触发有声播放
  userInteractionEvents.forEach((evt) => {
    document.addEventListener(evt, onUserInteraction, { passive: true });
  });
});

onUnmounted(() => {
  const el = rootEl.value;
  const videoEl = videoRef.value as any;

  el?.removeEventListener('fullscreenchange', onFullScreenChange);
  el?.removeEventListener('fullscreenerror', onFullScreenChange);
  videoEl?.removeEventListener('fullscreenchange', onFullScreenChange);
  videoEl?.removeEventListener('fullscreenerror', onFullScreenChange);

  // 清理用户交互监听
  userInteractionEvents.forEach((evt) => {
    document.removeEventListener(evt, onUserInteraction, { passive: true } as any);
  });
});

// ---------- autoplay 侦听 ----------
// 注意：浏览器策略要求 autoplay + unmuted 必须由用户交互触发，
// 纯自动播放场景下浏览器会阻止 unmuted 视频。此处不再强制 muted，
// 让浏览器自然处理（会自动阻止 unmuted autoplay，需用户点击播放）。
watch(
  () => props.autoplay,
  (val) => {
    if (videoRef.value && val) {
      videoRef.value.play().catch(() => {});
    }
  },
  { immediate: true }
);
</script>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.e-video-player {
  $e: &;
  position: relative;
  background-color: rgba($blue-night, 0.05);

  video {
    display: block;
    width: 100%;
    height: auto;
    object-fit: contain;
    outline: none;
    mix-blend-mode: darken;
  }

  // 全屏时浏览器使用黑色背景，darken 会把视频内容一并混合成黑色。
  &:fullscreen,
  &:-webkit-full-screen {
    width: 100vw !important;
    height: 100vh !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    aspect-ratio: auto !important;
    background: $black;

    video {
      width: 100% !important;
      height: 100% !important;
      min-width: 0 !important;
      min-height: 0 !important;
      max-width: none !important;
      max-height: none !important;
      object-fit: contain !important;
      object-position: center !important;
      mix-blend-mode: normal !important;
    }
  }

  video:fullscreen,
  video:-webkit-full-screen {
    width: 100vw !important;
    height: 100vh !important;
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: none !important;
    max-height: none !important;
    aspect-ratio: auto !important;
    background: $black;
    object-fit: contain !important;
    object-position: center !important;
    mix-blend-mode: normal !important;
  }

  &__center-buttons {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
  }

  &__center-button {
    width: 48px;
    height: 48px;
    border-radius: 24px;
    color: rgba($white, 0.9);
    background-color: rgba($black, 0.1);
    border: 1px solid rgba($white, 0.9);
  }

  &__controls {
    position: absolute;
    bottom: 0;
    z-index: 1;
    width: 100%;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 12px;
    background-color: #{rgba($black, 0.4)};
    color: $white;
    padding: 4px 16px;
  }

  &__time {
    width: 40px;
    flex-shrink: 0;
    font-size: 12px;
  }

  &__control {
    &-bar {
      position: relative;
      align-self: stretch;
      flex: 1 1;
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    &-track {
      width: 100%;
      background-color: rgba($blue-night, 0.5);
      height: 4px;
      border-radius: 2px;
    }

    &-thumb {
      position: absolute;
      top: 12px;
      width: 8px;
      height: 8px;
      background-color: $blue-night;
      border-radius: 4px;
    }

    &-progress {
      position: absolute;
      left: 0;
      top: 14px;
      height: 4px;
      background-color: $blue-night;
      border-radius: 2px;
    }

    &-hover-pop {
      position: absolute;
      bottom: 36px;
      transform: translateX(-50%);
      background-color: rgba($black, 0.6);
      color: $white;
      text-align: center;
      padding: 4px 8px;

      &:before {
        content: '';
        position: absolute;
        left: calc(50% - 5px);
        bottom: -4px;
        border-top: 5px solid rgba($black, 0.6);
        border-left: 5px solid transparent;
        border-right: 5px solid transparent;
      }
    }
  }

  &.as-bg {
    width: 100%;
    height: 100%;

    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
}

/* 控制栏淡入淡出动画（对应原来的 framer-motion barVariants） */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
